import type { ContactLinksQueryParams } from './useQueryContactLinks';
import type { FindSuppliersQueryParams } from './useQueryFindSuppliersByCreditorNumber';
import type { NetworkSuppliersQueryParams } from './useQueryNetworkSuppliers';
import type { SupplierIdentifierCheckParams } from './useQuerySupplierIdentifierCheck';
import type { SupplierRequestsQueryParams } from './useQuerySupplierRequests';
import type { SupplierResponsiblePersonOptionsQueryParams } from './useQuerySupplierResponsiblePersonOptions';
import type { SuppliersOverviewQueryParams } from './useQuerySuppliersOverview';
import type { SuppliersOverviewCountsQueryParams } from './useQuerySuppliersOverviewCounts';
import type { QueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

/**
 * Query key factory for supplier-related queries.
 */
export const queryKeysSupplier = {
  base: () => 'supplier' as const,
  checkIdentifier: (queryParams: SupplierIdentifierCheckParams | undefined) =>
    [queryKeysSupplier.base(), 'requests', 'check', queryParams] as const,
  findByCreditorNumber: (queryParams: FindSuppliersQueryParams | undefined) =>
    [queryKeysSupplier.base(), 'find', queryParams] as const,
  getActivationPublic: (requestId: UUID, token: string) =>
    [
      queryKeysSupplier.base(),
      'activation',
      'public',
      { requestId, token },
    ] as const,
  getActivationRequest: (requestId: UUID | undefined) =>
    requestId === undefined
      ? ([queryKeysSupplier.base(), 'activation'] as const)
      : ([queryKeysSupplier.base(), 'activation', { requestId }] as const),
  getActivationRequests: (deId: UUID | undefined) =>
    deId === undefined
      ? ([queryKeysSupplier.base(), 'activationRequests'] as const)
      : ([queryKeysSupplier.base(), 'activationRequests', { deId }] as const),
  getContactLink: (linkId: UUID) =>
    [queryKeysSupplier.base(), 'contactLink', { linkId }] as const,
  getContactLinkByDataExchangeId: (deId: UUID) =>
    [queryKeysSupplier.base(), 'contactLink', { deId }] as const,
  getContactLinks: (queryParams: Partial<ContactLinksQueryParams>) =>
    [queryKeysSupplier.base(), 'contactLink', 'list', queryParams] as const,
  getDetail: (supplierId: UUID) =>
    [queryKeysSupplier.base(), 'detail', { supplierId }] as const,
  getHistory: (supplierId: UUID, limit?: number, offset?: number) =>
    [
      queryKeysSupplier.base(),
      'detail',
      { supplierId },
      'history',
      { limit, offset },
    ] as const,
  getNetwork: (queryParams?: Partial<NetworkSuppliersQueryParams>) =>
    queryParams === undefined
      ? ([queryKeysSupplier.base(), 'network'] as const)
      : ([queryKeysSupplier.base(), 'network', queryParams] as const),
  getOverview: (queryParams?: Partial<SuppliersOverviewQueryParams>) =>
    queryParams === undefined
      ? ([queryKeysSupplier.base(), 'overview'] as const)
      : ([queryKeysSupplier.base(), 'overview', queryParams] as const),
  getOverviewCounts: (
    queryParams: Partial<SuppliersOverviewCountsQueryParams>,
  ) => [queryKeysSupplier.base(), 'overview', queryParams, 'counts'] as const,
  getPriority: (recipientCompanyId: UUID | undefined) =>
    recipientCompanyId === undefined
      ? ([queryKeysSupplier.base(), 'priority'] as const)
      : ([
          queryKeysSupplier.base(),
          'priority',
          { recipientCompanyId },
        ] as const),
  getRequests: (queryParams?: Partial<SupplierRequestsQueryParams>) =>
    queryParams === undefined
      ? ([queryKeysSupplier.base(), 'requests'] as const)
      : ([queryKeysSupplier.base(), 'requests', queryParams] as const),
  getResponsiblePersonOptions: (
    queryParams: Partial<SupplierResponsiblePersonOptionsQueryParams>,
  ) =>
    [
      queryKeysSupplier.base(),
      'responsiblePersonOptions',
      queryParams,
    ] as const,
} as const;

/**
 * Invalidates every query touched by submitting a supplier activation
 * request or plan: the activation requests list, the supplier overview, and
 * the supplier detail — the latter because the customer number in the
 * payload is shared with and persisted on the supplier detail page.
 *
 * Shared by useMutationPostSupplierActivation and
 * useMutationPostSupplierActivationPlan, which invalidate identically.
 */
export function invalidateSupplierActivationQueries(
  queryClient: QueryClient,
  deId: UUID,
) {
  void queryClient.invalidateQueries({
    queryKey: [queryKeysSupplier.base(), 'activation'],
  });
  void queryClient.invalidateQueries({
    queryKey: [queryKeysSupplier.base(), 'overview'],
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeysSupplier.getActivationRequests(deId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeysSupplier.getDetail(deId),
  });
}
